/**
 * InactivityTimer - Detects user inactivity and triggers a callback after a specified timeout.
 *
 * Used to close SSE connections when the user is idle to conserve resources.
 * The timer resets on any user interaction (click, touch, keydown, mousemove, scroll).
 */

export interface InactivityTimerConfig {
  timeoutMs: number;
  onTimeout: () => void;
  onActivity?: () => void;
}

export class InactivityTimer {
  private timeoutMs: number;
  private timerId: number = 0;
  private onTimeout: () => void;
  private onActivity?: () => void;
  private isInitialized = false;

  private readonly ACTIVITY_EVENTS = ['click', 'touchstart', 'keydown', 'mousemove', 'scroll'];

  constructor(config: InactivityTimerConfig) {
    this.timeoutMs = config.timeoutMs;
    this.onTimeout = config.onTimeout;
    this.onActivity = config.onActivity;
  }

  /**
   * Initialize the timer and start listening for user activity events.
   * Safe to call multiple times - will only initialize once.
   */
  initialize(): void {
    if (this.isInitialized || typeof window === 'undefined') return;
    this.isInitialized = true;

    this.ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, this.resetTimer, { passive: true }));

    this.resetTimer();
  }

  /**
   * Reset the inactivity timer. Called automatically on user activity,
   * but can also be called manually (e.g., when a response is received).
   */
  resetTimer = (): void => {
    if (this.timeoutMs <= 0) return;

    clearTimeout(this.timerId);
    this.timerId = window.setTimeout(() => {
      this.onTimeout();
    }, this.timeoutMs);

    this.onActivity?.();
  };

  /**
   * Clean up event listeners and clear the timer.
   * Call this when the component unmounts.
   */
  cleanup(): void {
    if (!this.isInitialized) return;

    clearTimeout(this.timerId);
    this.ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, this.resetTimer));
    this.isInitialized = false;
  }

  /**
   * Update the timeout duration. Restarts the timer with the new duration.
   */
  updateTimeout(timeoutMs: number): void {
    this.timeoutMs = timeoutMs;
    if (this.isInitialized) {
      this.resetTimer();
    }
  }
}
